import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "../components/PageHeader";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Gauge, Search, Save, Loader2, RefreshCw, AlertTriangle } from "lucide-react";

const TR_MAP = { "İ": "i", "I": "i", "ı": "i", "Ş": "s", "ş": "s", "Ğ": "g", "ğ": "g", "Ü": "u", "ü": "u", "Ö": "o", "ö": "o", "Ç": "c", "ç": "c" };
const norm = (s) => String(s ?? "").replace(/[İIıŞşĞğÜüÖöÇç]/g, (m) => TR_MAP[m]).toLowerCase();

export default function MakinaKapasitesi() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState({});
  const [q, setQ] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/stock/kapasite-list")
      .then((r) => { setRows(r.data || []); setEdits({}); })
      .catch((e) => toast.error(apiError(e.response?.data?.detail) || "Liste alınamadı"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const valOf = (r) => (edits[r.id] !== undefined ? edits[r.id] : (r.makina_kapasitesi ?? ""));
  const isMissing = (r) => {
    const v = valOf(r);
    return v === "" || v === null || Number(v) === 0;
  };

  const filtered = useMemo(() => {
    const nq = norm(q).trim();
    return rows.filter((r) => {
      if (onlyMissing && !isMissing(r) && edits[r.id] === undefined) return false;
      if (!nq) return true;
      return [r.kalip_kodu, r.urun_aciklamasi, r.makina].some((f) => norm(f).includes(nq));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, q, onlyMissing, edits]);

  const missingCount = rows.filter((r) => isMissing(r)).length;
  const changedCount = Object.keys(edits).length;

  const setVal = (id, v) => setEdits((e) => ({ ...e, [id]: v }));

  const save = async () => {
    const items = Object.entries(edits).map(([id, v]) => ({ id, makina_kapasitesi: v === "" ? null : Number(v) }));
    if (!items.length) { toast.info("Kaydedilecek değişiklik yok"); return; }
    setSaving(true);
    try {
      const res = await api.post("/stock/kapasite-bulk", { items });
      toast.success(`${res.data.updated} kalıp güncellendi`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Kaydedilemedi");
    } finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader title="Makina Kapasitesi (Toplu Giriş)"
        subtitle="Kalıpların günlük makina kapasitesini tek ekrandan doldurun. Tahmini kalıp değişim tarihi bu değerlerden hesaplanır." />

      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative min-w-[240px]">
            <Search size={15} className="absolute left-3 top-2.5 text-zinc-400" />
            <input data-testid="kapasite-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Kalıp kodu / açıklama / makine ara"
              className="w-full border border-zinc-300 rounded-sm pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-600 font-semibold cursor-pointer select-none">
            <input type="checkbox" data-testid="kapasite-only-missing" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} className="accent-amber-600" />
            Sadece eksik olanlar
          </label>
          <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-red-50 text-red-600 border border-red-200 rounded-full px-3 py-1" data-testid="kapasite-missing-count">
            <AlertTriangle size={13} /> {missingCount} kalıpta kapasite eksik
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="kapasite-refresh" className="flex items-center gap-1.5 text-sm text-zinc-600 hover:text-amber-700 font-semibold">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Yenile
          </button>
          {canEdit && (
            <button onClick={save} disabled={saving || changedCount === 0} data-testid="kapasite-save"
              className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white text-sm font-semibold px-4 py-2 rounded-sm transition-colors">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Kaydet {changedCount > 0 ? `(${changedCount})` : ""}
            </button>
          )}
        </div>
      </div>

      <div className="border border-zinc-200 bg-white rounded-sm overflow-auto" style={{ maxHeight: "calc(100vh - 240px)" }}>
        <table className="w-full data-table" data-testid="kapasite-table">
          <thead className="sticky top-0 z-20 bg-zinc-100">
            <tr>
              <th className="text-left">Sıra</th>
              <th className="text-left">Kalıp Kodu</th>
              <th className="text-left">Ürün Açıklaması</th>
              <th className="text-left">Makine</th>
              <th className="text-right">Günlük Makina Kapasitesi</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-10 text-zinc-400"><Loader2 className="animate-spin inline" size={18} /> Yükleniyor...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-10 text-zinc-400">Kayıt yok.</td></tr>
            ) : filtered.map((r) => {
              const missing = isMissing(r);
              return (
                <tr key={r.id} data-testid={`kapasite-row-${r.kalip_kodu}`} className={missing ? "bg-red-50/40" : ""}>
                  <td className="text-zinc-400">{r.sira_no ?? ""}</td>
                  <td className="mono font-bold text-zinc-800">{r.kalip_kodu}</td>
                  <td className="text-zinc-600">{r.urun_aciklamasi || "—"}</td>
                  <td className="text-zinc-600">{r.makina || "—"}</td>
                  <td className="text-right">
                    <input type="number" min="0" disabled={!canEdit}
                      data-testid={`kapasite-input-${r.kalip_kodu}`}
                      value={valOf(r)}
                      onChange={(e) => setVal(r.id, e.target.value)}
                      placeholder="—"
                      className={`w-32 text-right border rounded-sm px-2 py-1.5 text-sm mono focus:outline-none focus:ring-2 focus:ring-amber-600 disabled:bg-zinc-50 disabled:text-zinc-400 ${missing ? "border-red-300" : "border-zinc-300"}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-3 text-xs text-zinc-500" data-testid="kapasite-footer">
        {filtered.length} kalıp gösteriliyor · Toplam {rows.length}
        {!canEdit && <span className="ml-2 text-amber-600 font-semibold">(Yalnızca yönetici düzenleyebilir)</span>}
      </div>
    </>
  );
}
